/**
 * Ícono «ⓘ» con una ventanita que explica una métrica o cálculo (qué mide y cómo se calcula).
 * Se abre al pasar el cursor, con el teclado (foco) o con un toque en móvil. La ventanita se
 * pinta en un portal con posición fija para que no la recorten tablas con scroll.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ink } from '../admin/studioUi';
import { glossaryFor } from './metricGlossary';

const WIDTH = 280;

export default function InfoTip({ term, what, how, light }) {
    const entry = term ? glossaryFor(term) : null;
    const text = { what: what || entry?.what, how: how || entry?.how };
    const [pos, setPos] = useState(null);
    const ref = useRef(null);
    const openedAt = useRef(0);

    const show = useCallback(() => {
        openedAt.current = Date.now();
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const left = Math.max(8, Math.min(window.innerWidth - WIDTH - 8, rect.left + rect.width / 2 - WIDTH / 2));
        const below = rect.bottom + 8;
        const above = window.innerHeight - below < 180;
        setPos({ left, top: above ? undefined : below, bottom: above ? window.innerHeight - rect.top + 8 : undefined });
    }, []);
    const hide = useCallback(() => setPos(null), []);

    useEffect(() => {
        if (!pos) return undefined;
        const close = (event) => { if (ref.current && !ref.current.contains(event.target)) hide(); };
        window.addEventListener('scroll', hide, true);
        document.addEventListener('touchstart', close);
        return () => {
            window.removeEventListener('scroll', hide, true);
            document.removeEventListener('touchstart', close);
        };
    }, [pos, hide]);

    if (!text.what && !text.how) return null;
    return (
        <>
            <span
                ref={ref}
                role="button"
                tabIndex={0}
                aria-label={`Qué significa: ${text.what || ''}`}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
                onClick={(e) => {
                    e.stopPropagation();
                    /* En móvil el toque dispara foco y luego clic: no cerrar lo que se acaba de abrir. */
                    if (pos && Date.now() - openedAt.current > 400) hide();
                    else if (!pos) show();
                }}
                style={{ ...styles.icon, ...(light ? styles.iconLight : {}) }}
            >
                i
            </span>
            {pos && createPortal(
                <div role="tooltip" style={{ ...styles.tip, left: pos.left, top: pos.top, bottom: pos.bottom }}>
                    {text.what && <p style={styles.what}>{text.what}</p>}
                    {text.how && <p style={styles.how}><strong>Cómo se calcula: </strong>{text.how}</p>}
                </div>,
                document.body
            )}
        </>
    );
}

const styles = {
    icon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        marginLeft: 5,
        borderRadius: '50%',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.faint,
        color: ink.faint,
        fontSize: '0.6rem',
        fontWeight: 700,
        fontStyle: 'italic',
        fontFamily: 'Georgia, serif',
        lineHeight: 1,
        cursor: 'help',
        verticalAlign: 'middle',
        textTransform: 'none',
        letterSpacing: 0,
        flexShrink: 0,
        userSelect: 'none',
    },
    iconLight: { borderColor: ink.lineStrong, color: ink.muted },
    tip: {
        position: 'fixed',
        zIndex: 3000,
        width: WIDTH,
        padding: '10px 12px',
        borderRadius: 10,
        backgroundColor: '#1c1d21',
        color: '#ffffff',
        boxShadow: '0 10px 28px rgba(28, 29, 33, 0.3)',
        fontSize: '0.78rem',
        lineHeight: 1.45,
        textTransform: 'none',
        letterSpacing: 0,
        fontWeight: 400,
        textAlign: 'left',
        pointerEvents: 'none',
    },
    what: { margin: 0 },
    how: { margin: '6px 0 0', color: 'rgba(255,255,255,0.78)' },
};
